import { useEffect, useMemo } from "react";
import { IColumnProps } from "devextreme-react/data-grid";
import { IItemProps } from "devextreme-react/form";

import { PageProps, UpdateDomTitleForForm } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { createFormulaEditCellComponent } from "../../components/formulaEditor";
import { FormulaContextKeys } from "../../model/formula";

import { InactivatableCrudDataGrid } from "../common/InactivatableCrudDataGrid";

import { accountColumns, dummyAccountRow } from "./model/metaData";

const FORMULA_FIELDS = ["accountCodeFormula"];

const Accounts = (pageProps: PageProps) => {
  const logger = pageProps.loggerProvider.getLogger("Accounts");
  logger.debug("Rendering Accounts component");

  useEffect(() => {
    UpdateDomTitleForForm(pageProps);
  });

  const httpClient = pageProps.appUtil.httpClients["eAccountingService"];

  // Render the formula editor through the column's `editCellComponent` so the DataGrid's editing
  // controller supplies the current value and receives changes. A form-item template would bypass
  // that binding and open with an empty editor for existing rows.
  const formulaEditCellComponent = useMemo(
    () =>
      createFormulaEditCellComponent({
        httpClient,
        contextKey: FormulaContextKeys.TessituraContext,
        rootObjects: ["TransactionInfo"],
      }),
    [httpClient],
  );

  const columnConfigs = useMemo(() => {
    const configs = new Map<string, IColumnProps>(accountColumns);
    FORMULA_FIELDS.forEach((field) => {
      const existing = configs.get(field) ?? { dataField: field };
      configs.set(field, { ...existing, editCellComponent: formulaEditCellComponent });
    });
    return configs;
  }, [formulaEditCellComponent]);

  const editorItemOptions = useMemo(() => {
    const options = new Map<string, IItemProps>([
      [
        "active",
        {
          editorType: "dxCheckBox",
        },
      ],
    ]);
    return options;
  }, []);

  return (
    <MainCard title="COA GL Accounts">
      <InactivatableCrudDataGrid
        pageProps={pageProps}
        logger={logger}
        dummyRow={dummyAccountRow}
        baseUrl="accounts"
        isOrganizationData={true}
        keyExpr="accountName"
        formTitle="COA GL Account"
        formColCount={2}
        formColSpan={2}
        allowAdding={true}
        allowUpdating={true}
        allowDeleting={true}
        columnConfigs={columnConfigs}
        editorItemOptions={editorItemOptions}
      ></InactivatableCrudDataGrid>
    </MainCard>
  );
};

export default Accounts;
